"use client";
import { NextLink } from "@components/ui/next-link/NextLink";
import { useRouter } from "@i18n/routing";
import { getPagePathByLocale, homePage, shoppingBagPage } from "@lib/constants/pagePaths";
import { Text } from "@ecco/ui/text";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Container } from "@ecco/ui/container";
import { ECCOLogo } from "@ecco/ui/ecco-logo";
import type { ReactElement } from "react";

interface CheckoutNavigationProps {
    locale: string;
    backText: string | null;
    homeButtonLabel: string | null;
}

export function CheckoutNavigation({
    locale,
    backText,
    homeButtonLabel,
}: CheckoutNavigationProps): ReactElement {
    const { push } = useRouter();

    const handleBack = () => {
        push(getPagePathByLocale(shoppingBagPage, locale));
    };

    return (
        <Box bg="gray.50" position="sticky" zIndex={10}>
            <Container px={0} maxW="container.largeDesktop" mx="auto">
                <Box
                    data-testid="checkout-layout"
                    mt={[3, 4, 6]}
                    mx={[3, 4, 6]}
                    style={{ display: "flex", gap: 1 }}
                >
                    <NextLink
                        href={getPagePathByLocale(homePage, locale)}
                        alignSelf="center"
                        aria-label={homeButtonLabel ?? undefined}
                        display="flex"
                        bg="white"
                        h={["10", "10"]}
                        data-testid="homebutton"
                        px={["4"]}
                        py={["4", "2"]}
                        justifyContent="center"
                        alignItems="center"
                        boxShadow="md"
                        _hover={{ color: "gray.500" }}
                    >
                        <ECCOLogo />
                    </NextLink>
                    <Button
                        aria-label="Back Button"
                        data-testid="backbutton"
                        variant="solid"
                        color="black"
                        display="flex"
                        h={["10", "10"]}
                        px="3"
                        py={["3", "4"]}
                        justifyContent="center"
                        alignItems="center"
                        gap={["2", "3"]}
                        boxShadow="md"
                        onClick={handleBack}
                        bg="white"
                    >
                        {backText && (
                            <Text
                                fontSize={["mobileBodyTextSmall", "desktopBodyTextSmall"]}
                                fontWeight="bold"
                            >
                                {backText}
                            </Text>
                        )}
                    </Button>
                </Box>
            </Container>
        </Box>
    );
}
